import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';
import { Icon } from '../ui/Icon';
import styles from './Connections.module.css';
interface IncomingConnection {
  id: string;
  name: string;
  enabled: boolean;
  revision: number;
  generation: number;
  revoked_at: string | null;
  allow_read: boolean;
  allow_write_requests: boolean;
  allow_agent_tasks: boolean;
  token_prefix: string;
  mcpUrl: string;
  agentCardUrl: string;
  a2aUrl: string;
  instructions: string;
}
interface IncomingRequest {
  clientName?: string;
  target?: { id: string; name: string; object: string };
  id: string;
  connection_id: string;
  protocol: string;
  kind: string;
  request_hash: string;
  revision: number;
  payload: Record<string, unknown>;
  status: string;
  result: { text?: string; record?: unknown } | null;
  released_text: string | null;
  safe_error: string | null;
  conversation_id: string | null;
}
type IssuedAccess = { connection: IncomingConnection; token: string };
const incomingStarters = [
  { id: 'coding', title: 'Coding assistant', name: 'My coding assistant', icon: 'tools',
    description: 'Let your coding assistant consult CRM records or propose changes for you to review.' },
  { id: 'brain', title: 'Your Second Brain', name: 'My Second Brain', icon: 'brain',
    description: 'Let your knowledge system consult Resonance or ask Echo to work on a task.' },
  { id: 'agent', title: 'Another AI agent', name: '', icon: 'message-circle',
    description: 'Let another agent submit a task to Echo and receive the response you choose to share.' },
  { id: 'custom', title: 'Another app or service', name: '', icon: 'plug',
    description: 'Give a compatible MCP or A2A client its own permissions and access key.' },
];
function useIncomingScope() {
  const { bootstrap } = useAuth();
  return `${bootstrap?.workspace.id}:${bootstrap?.user?.id}`;
}
export function IncomingConnections() {
  const scope = useIncomingScope(),
    cache = useQueryClient();
  const clients = useQuery({
    queryKey: ['incoming-connections', scope],
    queryFn: () =>
      request<{ items: IncomingConnection[] }>('/v1/incoming-connections'),
    refetchInterval: 30000,
  });
  const recent = useQuery({
    queryKey: ['incoming-requests', scope],
    queryFn: () =>
      request<{ items: IncomingRequest[] }>('/v1/incoming-requests'),
    refetchInterval: 10000,
  });
  const [adding, setAdding] = useState(false),
    [issued, setIssued] = useState<IssuedAccess | null>(null),
    [clientName, setClientName] = useState('');
  const start = (name: string) => {
    setClientName(name);
    setAdding(true);
  };
  const refresh = () => {
    void cache.invalidateQueries({ queryKey: ['incoming-connections'] });
    void cache.invalidateQueries({ queryKey: ['incoming-requests'] });
    void cache.invalidateQueries({ queryKey: ['navigation-status'] });
  };
  return (
    <>
      <section className={styles.introduction} aria-labelledby="incoming-intro">
        <div className={styles.introductionCopy}>
          <span className={styles.eyebrow}>
            Other tools and agents → Resonance
          </span>
          <h2 id="incoming-intro">Let your other systems work with Echo.</h2>
          <p>
            Give a named client its own access key. An MCP client can use the
            CRM abilities you allow. Another A2A agent can submit a task for
            Echo to work on.
          </p>
          <div className={styles.protocolExplanation}>
            <div>
              <div className={styles.protocolHeading}>
                <span className={`${styles.protocolMark} ${styles.mcpMark}`} aria-hidden="true" />
                <strong>Incoming MCP</strong>
              </div>
              <p>
                Your coding assistant or another compatible client can search
                CRM records, retrieve a record, or request a reviewed CRM
                change.
              </p>
            </div>
            <div>
              <div className={styles.protocolHeading}>
                <span className={`${styles.protocolMark} ${styles.a2aMark}`} aria-hidden="true" />
                <strong>Incoming A2A</strong>
              </div>
              <p>
                Another agent submits a brief. You review it before Echo starts,
                then review the response before it is shared back.
              </p>
            </div>
          </div>
        </div>
        <section className={styles.startSection} aria-labelledby="incoming-start-title">
          <div>
            <h2 id="incoming-start-title">Connect a client</h2>
            <p className={styles.muted}>Choose a system you want to give access to Resonance.</p>
          </div>
          <div className={styles.starterGrid}>
            {incomingStarters.map((starter) => <button key={starter.id} type="button"
              className={styles.starterCard} onClick={() => start(starter.name)}>
              <Icon name={starter.icon} size={24} />
              <strong>{starter.title}</strong>
              <span>{starter.description}</span>
              <span className={styles.starterAction}>Allow this client <Icon name="arrow-right" size={16} /></span>
            </button>)}
          </div>
        </section>
      </section>
      {(adding || issued) && (
        <IncomingSetupDialog
          initialName={clientName}
          issued={issued}
          onClose={() => { setAdding(false); setIssued(null); }}
          onCreated={(value) => {
            setIssued(value);
            setAdding(false);
            refresh();
          }}
        />
      )}
      <div className={styles.savedHeading}>
        <h2>Clients with incoming access</h2>
        <p className={styles.muted}>
          Each key is private to you, limited to its selected permissions, and
          individually revocable.
        </p>
      </div>
      {clients.isPending && <p>Loading incoming access…</p>}
      {clients.isError && (
        <p role="alert">
          {clients.error.message}{' '}
          <button onClick={() => void clients.refetch()}>Retry</button>
        </p>
      )}
      {clients.data?.items.length === 0 && (
        <section className={styles.card}>
          <strong>No incoming clients yet</strong>
          <p className={styles.muted}>
            Choose a starting point above to create a key and get the connection URLs.
            This does not make your workspace public.
          </p>
        </section>
      )}
      {clients.data?.items.map((client) => (
        <IncomingClient
          key={client.id}
          client={client}
          onIssued={setIssued}
          refresh={refresh}
        />
      ))}
      <div className={styles.savedHeading}>
        <h2>Incoming requests</h2>
        <p className={styles.muted}>
          Review write requests and agent tasks, inspect their work, and choose
          responses to share.
        </p>
      </div>
      {recent.isError && <p role="alert">{recent.error.message}</p>}
      {recent.data?.items.length === 0 && (
        <p className={styles.muted}>
          Requests will appear here and pending approvals will also appear in
          Home.
        </p>
      )}
      {recent.data?.items.map((item) => (
        <IncomingRequestCard
          key={item.id}
          requestId={item.id}
          clientName={
            clients.data?.items.find((c) => c.id === item.connection_id)?.name
          }
        />
      ))}
    </>
  );
}
function IncomingSetupDialog({ initialName, issued, onClose, onCreated }: {
  initialName: string;
  issued: IssuedAccess | null;
  onClose: () => void;
  onCreated: (value: IssuedAccess) => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const title = useId();
  const backdropPress = useRef(false);
  const [protocol, setProtocol] = useState<'' | 'mcp' | 'a2a'>('');
  const [name, setName] = useState(initialName),
    [allowRead, setRead] = useState(false),
    [allowWriteRequests, setWrite] = useState(false),
    [allowAgentTasks, setTasks] = useState(false),
    [instructions, setInstructions] = useState('');
  useEffect(() => {
    const element = dialog.current!;
    const previous = document.activeElement as HTMLElement | null;
    element.showModal();
    return () => {
      if (element.open) element.close();
      previous?.focus({ preventScroll: true });
    };
  }, []);
  useEffect(() => {
    const element = dialog.current;
    element?.querySelector<HTMLElement>('[data-incoming-focus]')?.focus({ preventScroll: true });
    if (element) element.scrollTop = 0;
  }, [protocol, issued?.connection.id]);
  const hasPermission = protocol === 'mcp' ? allowRead || allowWriteRequests : protocol === 'a2a' && allowAgentTasks;
  const create = useMutation({
    mutationFn: () => {
      if (!protocol || !name.trim() || !hasPermission) throw new Error('Name the client and select at least one permission.');
      return request<IssuedAccess>('/v1/incoming-connections', {
        method: 'POST',
        body: {
          name,
          allowRead: protocol === 'mcp' && allowRead,
          allowWriteRequests: protocol === 'mcp' && allowWriteRequests,
          allowAgentTasks: protocol === 'a2a' && allowAgentTasks,
          instructions: protocol === 'a2a' ? instructions : '',
        },
      });
    },
    onSuccess: onCreated,
  });
  const dismiss = () => { if (!create.isPending) onClose(); };
  const outsideDialog = (element: HTMLDialogElement, x: number, y: number) => {
    const bounds = element.getBoundingClientRect();
    return x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom;
  };
  return <dialog ref={dialog} className={styles.setupDialog} aria-labelledby={title}
    onCancel={(event) => { event.preventDefault(); dismiss(); }}
    onPointerDown={(event) => {
      backdropPress.current = event.target === event.currentTarget && outsideDialog(event.currentTarget, event.clientX, event.clientY);
    }}
    onClick={(event) => {
      const clickedBackdrop = backdropPress.current && event.target === event.currentTarget && outsideDialog(event.currentTarget, event.clientX, event.clientY);
      backdropPress.current = false;
      if (clickedBackdrop && !issued) dismiss();
    }}>
    <div className={styles.card}>
      <header className={styles.modalHeading}>
        <h2 id={title}>{issued ? `Save the key for ${issued.connection.name}` : 'Allow an incoming client'}</h2>
        <button type="button" aria-label="Close incoming setup" disabled={create.isPending} onClick={dismiss}>
          <Icon name="x" size={18} />
        </button>
      </header>
      {issued ? <IssuedKey value={issued} onDismiss={dismiss} /> : <form className={styles.setupFields}
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          if (!create.isPending && protocol && name.trim() && hasPermission) create.mutate();
        }}>
        <fieldset className={styles.setupFields} disabled={create.isPending}>
          {!protocol ? <>
            <h3 className={styles.protocolChoiceTitle}>Choose a connection type</h3>
            <div className={styles.protocolChoices}>
              <button type="button" className={styles.protocolChoice} data-incoming-focus onClick={() => setProtocol('mcp')}>
                <span className={styles.protocolHeading}>
                  <span className={`${styles.protocolMark} ${styles.mcpMark}`} aria-hidden="true" />
                  <strong>MCP</strong>
                </span>
                <strong>CRM tools and records</strong>
                <span>Let a client search your workspace records or propose CRM changes for you to review.</span>
                <span className={styles.protocolChoiceAction}>Allow an MCP client <Icon name="arrow-right" size={16} /></span>
              </button>
              <button type="button" className={styles.protocolChoice} onClick={() => setProtocol('a2a')}>
                <span className={styles.protocolHeading}>
                  <span className={`${styles.protocolMark} ${styles.a2aMark}`} aria-hidden="true" />
                  <strong>A2A</strong>
                </span>
                <strong>Tasks for Echo</strong>
                <span>Let another agent submit a task. You review the brief and choose which response to share.</span>
                <span className={styles.protocolChoiceAction}>Allow an A2A agent <Icon name="arrow-right" size={16} /></span>
              </button>
            </div>
          </> : <>
            <strong>{protocol === 'mcp' ? 'MCP · CRM tools and records' : 'A2A · Tasks for Echo'}</strong>
            <label>
              Client name
              <input data-incoming-focus required maxLength={200}
                placeholder={protocol === 'mcp' ? 'My coding assistant' : 'My research agent'}
                value={name} onChange={(event) => setName(event.target.value)} />
            </label>
            <fieldset className={styles.incomingPermissions}>
              <legend>What can this client do?</legend>
              {protocol === 'mcp' ? <>
                <label>
                  <input type="checkbox" checked={allowRead} onChange={(event) => setRead(event.target.checked)} />
                  <span><strong>Read workspace CRM records</strong>
                    <small>Search and retrieve CRM records directly, without approving every read. This grants access across this workspace’s CRM records.</small>
                  </span>
                </label>
                <label>
                  <input type="checkbox" checked={allowWriteRequests} onChange={(event) => setWrite(event.target.checked)} />
                  <span><strong>Request CRM changes</strong>
                    <small>Propose creating or updating a record. You review the exact fields before a change runs.</small>
                  </span>
                </label>
              </> : <label>
                <input type="checkbox" checked={allowAgentTasks} onChange={(event) => setTasks(event.target.checked)} />
                <span><strong>Submit tasks to Echo</strong>
                  <small>You approve each brief before AI work starts and review the response before sharing it.</small>
                </span>
              </label>}
            </fieldset>
            {!hasPermission && <p className={styles.muted}>Select at least one permission to create this client’s access key.</p>}
            {protocol === 'a2a' && <label>
              Guidance for tasks from this client
              <textarea maxLength={8000} value={instructions} onChange={(event) => setInstructions(event.target.value)}
                placeholder="Explain what this client works on and the boundaries Echo should follow." />
            </label>}
          </>}
        </fieldset>
        <div className={styles.modalActions}>
          <button type="button" disabled={create.isPending} onClick={dismiss}>Cancel</button>
          {protocol && <button type="submit" className={styles.primaryButton}
            disabled={create.isPending || !name.trim() || !hasPermission}>
            {create.isPending ? 'Creating key…' : 'Create access key'}
          </button>}
        </div>
        {create.isError && <p role="alert" className={styles.error}>{create.error.message}</p>}
      </form>}
    </div>
  </dialog>;
}
function IssuedKey({
  value,
  onDismiss,
}: {
  value: { connection: IncomingConnection; token: string };
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false),
    [error, setError] = useState('');
  return (
    <div className={styles.connectionFields} aria-label="New incoming access key">
      <p>
        This key is shown once. Copy it into the client’s secure connection
        settings. Keep it private; you can replace or revoke it below.
      </p>
      <label>
        Access key
        <input
          type="password"
          readOnly
          value={value.token}
          autoComplete="off"
        />
      </label>
      <div className={styles.row}>
        <button
          type="button"
          data-incoming-focus
          onClick={() => {
            void navigator.clipboard
              .writeText(value.token)
              .then(() => setCopied(true))
              .catch(() =>
                setError(
                  'Clipboard is unavailable. Select the key field and copy it manually.',
                ),
              );
          }}
        >
          {copied ? 'Copied' : 'Copy key'}
        </button>
        <button type="button" className={styles.primaryButton} onClick={onDismiss}>I’ve saved the key</button>
      </div>
      {error && <p role="alert">{error}</p>}
      <ConnectionAddresses client={value.connection} />
    </div>
  );
}
function ConnectionAddresses({ client }: { client: IncomingConnection }) {
  return (
    <div className={styles.connectionFields}>
      {(client.allow_read || client.allow_write_requests) && (
        <label>
          MCP endpoint
          <input readOnly value={client.mcpUrl} />
        </label>
      )}
      {client.allow_agent_tasks && (
        <>
          <label>
            A2A Agent Card URL
            <input readOnly value={client.agentCardUrl} />
          </label>
          <label>
            A2A endpoint
            <input readOnly value={client.a2aUrl} />
          </label>
        </>
      )}
      <p className={styles.muted}>
        Set the client’s Authorization header to{' '}
        <code>Bearer YOUR_ACCESS_KEY</code>. The Agent Card also requires this
        key. Use these URLs and a dedicated incoming key rather than your
        Resonance sign-in token.
      </p>
      {(client.allow_read || client.allow_write_requests) && (
        <details>
          <summary>MCP configuration example</summary>
          <pre className={styles.result}>
            {JSON.stringify(
              {
                mcpServers: {
                  resonance: {
                    url: client.mcpUrl,
                    headers: { Authorization: 'Bearer YOUR_ACCESS_KEY' },
                  },
                },
              },
              null,
              2,
            )}
          </pre>
        </details>
      )}
    </div>
  );
}
function IncomingClient({
  client: c,
  onIssued,
  refresh,
}: {
  client: IncomingConnection;
  onIssued: (value: { connection: IncomingConnection; token: string }) => void;
  refresh: () => void;
}) {
  const [confirm, setConfirm] = useState<'rotate' | 'revoke' | null>(null);
  const action = useMutation({
    mutationFn: (kind: 'rotate' | 'revoke') =>
      request<{ connection: IncomingConnection; token?: string }>(
        `/v1/incoming-connections/${c.id}/${kind}`,
        { method: 'POST', body: { expectedRevision: c.revision } },
      ),
    onSuccess: (value) => {
      if (value.token)
        onIssued({ connection: value.connection, token: value.token });
      setConfirm(null);
      refresh();
      action.reset();
    },
  });
  return (
    <section className={styles.card}>
      <div className={styles.row}>
        <strong>{c.name}</strong>
        <span>
          <i className={styles.dot} data-ready={c.enabled && !c.revoked_at} />
          {c.revoked_at ? 'Revoked' : 'Active'}
        </span>
      </div>
      <p className={styles.muted}>
        {[
          c.allow_read ? 'CRM reads' : null,
          c.allow_write_requests ? 'Reviewed CRM writes' : null,
          c.allow_agent_tasks ? 'Reviewed agent tasks' : null,
        ]
          .filter(Boolean)
          .join(' · ')}{' '}
        · Key {c.token_prefix}…
      </p>
      {!c.revoked_at && (
        <>
          <ConnectionAddresses client={c} />
          {c.instructions && <p>Task guidance: {c.instructions}</p>}
          <div className={styles.row}>
            <button onClick={() => setConfirm('rotate')}>Replace key</button>
            <button onClick={() => setConfirm('revoke')}>Revoke access</button>
          </div>
        </>
      )}
      {confirm && (
        <div>
          <p>
            {confirm === 'rotate'
              ? 'Replace this key? The old key will stop working and pending approvals will be cancelled.'
              : 'Revoke this client? New access and pending approvals stop. Work already accepted may have run; revocation does not undo it.'}
          </p>
          <button
            disabled={action.isPending}
            onClick={() => action.mutate(confirm)}
          >
            Confirm {confirm === 'rotate' ? 'replacement' : 'revocation'}
          </button>
          <button disabled={action.isPending} onClick={() => setConfirm(null)}>
            Keep current access
          </button>
        </div>
      )}
      {action.isError && <p role="alert">{action.error.message}</p>}
    </section>
  );
}
export function IncomingRequestCard({
  requestId,
  clientName,
}: {
  requestId: string;
  clientName?: string | undefined;
}) {
  const scope = useIncomingScope(),
    cache = useQueryClient();
  const q = useQuery({
    queryKey: ['incoming-request', scope, requestId],
    queryFn: () =>
      request<IncomingRequest>(`/v1/incoming-requests/${requestId}`),
    refetchInterval: (q) =>
      ['completed', 'rejected', 'cancelled', 'unknown'].includes(
        q.state.data?.status ?? '',
      )
        ? false
        : 5000,
  });
  const action = useMutation({
    mutationFn: (decision: 'approve' | 'reject' | 'cancel') =>
      request<IncomingRequest>(
        `/v1/incoming-requests/${requestId}/${decision === 'cancel' ? 'cancel' : 'decision'}`,
        {
          method: 'POST',
          body: {
            expectedRevision: q.data!.revision,
            ...(decision !== 'cancel'
              ? { requestHash: q.data!.request_hash, decision }
              : {}),
          },
        },
      ),
    onSuccess: (value) => {
      cache.setQueryData(['incoming-request', scope, requestId], value);
      void cache.invalidateQueries({ queryKey: ['incoming-requests'] });
      void cache.invalidateQueries({ queryKey: ['daily-brief'] });
      void cache.invalidateQueries({ queryKey: ['conversations'] });
      if (value.status === 'completed' && value.kind !== 'agent_task') {
        for (const key of ['records', 'schema', 'timeline'])
          void cache.invalidateQueries({ queryKey: [key] });
      }
    },
  });
  if (!q.data)
    return (
      <p role="status">
        {q.isError
          ? 'This incoming request is unavailable.'
          : 'Loading incoming request…'}
      </p>
    );
  const r = q.data;
  return (
    <article className={styles.card}>
      <div className={styles.row}>
        <strong>
          {clientName ?? r.clientName ?? 'Incoming client'} ·{' '}
          {r.kind === 'agent_task' ? 'Agent task' : r.kind.replaceAll('_', ' ')}
        </strong>
        <span>{r.status.replaceAll('_', ' ')}</span>
      </div>
      {r.target && (
        <p>
          Target:{' '}
          <a
            href={`/app/o/${encodeURIComponent(r.target.object)}/${r.target.id}`}
          >
            {r.target.name}
          </a>
        </p>
      )}
      <pre className={styles.result}>{JSON.stringify(r.payload, null, 2)}</pre>
      {r.status === 'awaiting_approval' && (
        <>
          <p className={styles.muted}>
            {r.kind === 'agent_task'
              ? 'Approve this exact task to run with your existing AI funding or saved personal key. You will review the response before sharing it.'
              : 'Approve the exact CRM mutation shown above. This changes real workspace records.'}
          </p>
          <div className={styles.row}>
            <button
              disabled={action.isPending}
              onClick={() => action.mutate('approve')}
            >
              {r.kind === 'agent_task'
                ? 'Approve and run task'
                : 'Approve this CRM change'}
            </button>
            <button
              disabled={action.isPending}
              onClick={() => action.mutate('reject')}
            >
              Reject
            </button>
          </div>
        </>
      )}
      {r.status === 'awaiting_release' && (
        <IncomingResponseReview key={`${r.id}:${r.revision}`} item={r} />
      )}
      {r.status === 'completed' && (
        <div>
          <strong>
            {r.kind === 'agent_task'
              ? 'Shared response'
              : 'Completed mutation receipt'}
          </strong>
          <pre className={styles.result}>
            {r.released_text ?? JSON.stringify(r.result, null, 2)}
          </pre>
        </div>
      )}
      {['running', 'awaiting_release', 'cancel_requested'].includes(
        r.status,
      ) && (
        <button
          disabled={action.isPending || r.status === 'cancel_requested'}
          onClick={() => action.mutate('cancel')}
        >
          {r.status === 'awaiting_release'
            ? 'Withhold response and close task'
            : 'Request cancellation'}
        </button>
      )}
      {r.conversation_id && (
        <a href={`/app/c/${r.conversation_id}`}>
          Open the task’s chat and action reviews
        </a>
      )}
      {r.safe_error && <p role="status">{r.safe_error}</p>}
      {action.isError && <p role="alert">{action.error.message}</p>}
    </article>
  );
}
function IncomingResponseReview({ item }: { item: IncomingRequest }) {
  const scope = useIncomingScope(),
    cache = useQueryClient();
  const [text, setText] = useState(item.result?.text ?? '');
  const share = useMutation({
    mutationFn: () =>
      request<IncomingRequest>(`/v1/incoming-requests/${item.id}/release`, {
        method: 'POST',
        body: { expectedRevision: item.revision, text },
      }),
    onSuccess: (value) => {
      cache.setQueryData(['incoming-request', scope, item.id], value);
      void cache.invalidateQueries({ queryKey: ['incoming-requests'] });
    },
  });
  return (
    <div className={styles.connectionFields}>
      <strong>Review what goes back to the client</strong>
      <p className={styles.muted}>
        Only the text you share here is returned. Internal chat, tool results,
        and drafts stay in Resonance. Agent-written claims are not independent
        proof of external delivery.
      </p>
      <label>
        Response
        <textarea
          value={text}
          maxLength={32000}
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <button
        disabled={share.isPending || !text.trim()}
        onClick={() => share.mutate()}
      >
        Share this response
      </button>
      {share.isError && <p role="alert">{share.error.message}</p>}
    </div>
  );
}
